import React, { useEffect, useRef, useState } from 'react';
import API from '../utils/api';

const SECTIONS = [
  { key: 'notice', icon: '📢', label: '内部通知', empty: '暂无内部通知' },
  { key: 'phrase', icon: '📚', label: '物业管理学习资料', empty: '暂无学习资料' },
  { key: 'template', icon: '📄', label: '物业管理常用模板', empty: '暂无常用模板' },
  { key: 'memos', icon: '📝', label: '我的备忘录', empty: '暂无备忘录' }
];

const MAX_FILES = 5;
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const FILE_ACCEPT = '.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.pdf,image/jpeg,image/png,image/gif,image/webp,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf';

function fileSize(bytes) {
  const size = Number(bytes || 0);
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / 1024 / 1024).toFixed(1)} MB`;
}

function fileIcon(file) {
  if (String(file.mime_type || '').startsWith('image/')) return '🖼️';
  if (String(file.original_name || '').toLowerCase().endsWith('.pdf')) return '📕';
  return '📘';
}

export default function InfoPage({ user }) {
  const [active, setActive] = useState('notice');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', content: '' });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const fileInput = useRef(null);

  const isNoticeManager = ['admin', 'general_manager', 'manager', 'steward_chief'].includes(user.role);
  const canManage = active === 'memos' || (active === 'notice' ? isNoticeManager : user.role === 'admin');
  const supportsFiles = active === 'phrase' || active === 'template';
  const current = SECTIONS.find(section => section.key === active);

  const load = async () => {
    setLoading(true);
    try {
      const url = active === 'memos' ? '/info/memos' : `/info/${active}`;
      const res = await API.get(url);
      setItems(res.data || []);
    } catch (err) {
      alert(err.response?.data?.message || '获取信息失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setShowForm(false);
    setEditing(null);
    setForm({ title: '', content: '' });
    setSelectedFiles([]);
    load();
  }, [active]);

  const openAdd = () => {
    setEditing(null);
    setForm({ title: '', content: '' });
    setSelectedFiles([]);
    setShowForm(true);
  };

  const openEdit = item => {
    setEditing(item);
    setForm({ title: item.title, content: item.content || '' });
    setSelectedFiles([]);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const chooseFiles = event => {
    const files = Array.from(event.target.files || []);
    const existingCount = editing?.attachments?.length || 0;
    if (files.length + existingCount > MAX_FILES) {
      event.target.value = '';
      return alert(`每条资料最多上传${MAX_FILES}个文件`);
    }
    const tooLarge = files.find(file => file.size > MAX_FILE_SIZE);
    if (tooLarge) {
      event.target.value = '';
      return alert(`“${tooLarge.name}”超过10MB，请压缩后再上传`);
    }
    setSelectedFiles(files);
  };

  const save = async () => {
    if (!form.title.trim()) return alert('请填写标题');
    const existingFiles = editing?.attachments?.length || 0;
    if (!form.content.trim() && !selectedFiles.length && !existingFiles) return alert('请填写内容或至少上传一个文件');
    const base = active === 'memos' ? '/info/memos' : `/info/${active}`;
    setSaving(true);
    try {
      if (supportsFiles) {
        const data = new FormData();
        data.append('title', form.title);
        data.append('content', form.content);
        selectedFiles.forEach(file => data.append('files', file));
        if (editing) await API.put(`${base}/${editing.id}`, data);
        else await API.post(base, data);
      } else if (editing) {
        await API.put(`${base}/${editing.id}`, form);
      } else {
        await API.post(base, form);
      }
      setShowForm(false);
      setEditing(null);
      setForm({ title: '', content: '' });
      setSelectedFiles([]);
      await load();
    } catch (err) {
      alert(err.response?.data?.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const removeAttachment = async file => {
    if (!editing || !window.confirm(`确定删除附件“${file.original_name}”吗？`)) return;
    try {
      await API.delete(`/info/${active}/${editing.id}/attachments/${file.id}`);
      const attachments = (editing.attachments || []).filter(item => item.id !== file.id);
      setEditing({ ...editing, attachments });
      setItems(list => list.map(item => item.id === editing.id ? { ...item, attachments } : item));
    } catch (err) {
      alert(err.response?.data?.message || '删除附件失败');
    }
  };

  const remove = async item => {
    if (!window.confirm(`确定删除“${item.title}”吗？附件也会一并删除。`)) return;
    const base = active === 'memos' ? '/info/memos' : `/info/${active}`;
    try {
      await API.delete(`${base}/${item.id}`);
      await load();
    } catch (err) {
      alert(err.response?.data?.message || '删除失败');
    }
  };

  const copyText = async item => {
    const text = `${item.title}\n${item.content || ''}`;
    try {
      await navigator.clipboard.writeText(text);
      alert('已复制');
    } catch (_) {
      const area = document.createElement('textarea');
      area.value = text;
      document.body.appendChild(area);
      area.select();
      document.execCommand('copy');
      document.body.removeChild(area);
      alert('已复制');
    }
  };

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div style={s.headerTitle}>信息中心</div>
        <div style={s.headerSub}>内部通知 · 学习资料 · 常用模板 · 个人备忘</div>
      </div>

      <div style={s.grid}>
        {SECTIONS.map(section => (
          <button key={section.key} style={{ ...s.sectionBtn, ...(active === section.key ? s.sectionBtnActive : {}) }} onClick={() => setActive(section.key)}>
            <span style={s.sectionIcon}>{section.icon}</span>
            <span>{section.label}</span>
          </button>
        ))}
      </div>

      <div style={s.toolbar}>
        <div style={s.title}>{current.icon} {current.label}</div>
        {canManage && <button style={s.addBtn} onClick={openAdd}>{supportsFiles ? '＋上传资料' : '＋新增'}</button>}
      </div>

      {showForm && (
        <div style={s.formCard}>
          <div style={s.formTitle}>{editing ? '编辑' : '新增'}{current.label}</div>
          <input style={s.input} maxLength={200} placeholder="标题（必填）" value={form.title} onChange={event => setForm({ ...form, title: event.target.value })} />
          <textarea style={s.textarea} rows={6} placeholder={supportsFiles ? '资料说明（可不填）' : '请输入内容'} value={form.content} onChange={event => setForm({ ...form, content: event.target.value })} />

          {supportsFiles && (
            <div style={s.uploadArea}>
              <input ref={fileInput} type="file" accept={FILE_ACCEPT} multiple onChange={chooseFiles} style={{ display: 'none' }} />
              <button type="button" style={s.fileBtn} onClick={() => fileInput.current?.click()}>📎 选择图片、Word或PDF</button>
              <div style={s.fileHint}>每条最多5个文件，每个不超过10MB</div>
              {!!selectedFiles.length && (
                <div style={s.selectedList}>
                  {selectedFiles.map(file => <div key={`${file.name}-${file.size}`}>待上传：{file.name}（{fileSize(file.size)}）</div>)}
                </div>
              )}
              {!!editing?.attachments?.length && (
                <div style={s.selectedList}>
                  {editing.attachments.map(file => (
                    <div key={file.id} style={s.existingFile}>
                      <span>已有：{file.original_name}</span>
                      <button type="button" style={s.removeFileBtn} onClick={() => removeAttachment(file)}>删除附件</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div style={s.formActions}>
            <button style={s.cancelBtn} onClick={() => setShowForm(false)} disabled={saving}>取消</button>
            <button style={s.saveBtn} onClick={save} disabled={saving}>{saving ? '正在上传...' : '保存'}</button>
          </div>
        </div>
      )}

      {loading ? <div style={s.empty}>加载中...</div> : items.length === 0 ? (
        <div style={s.empty}>{current.empty}</div>
      ) : (
        <div style={s.list}>
          {items.map(item => (
            <div key={item.id} style={s.card}>
              <div style={s.cardTitle}>{item.title}</div>
              {!!item.content && <div style={s.cardContent}>{item.content}</div>}
              {!!item.attachments?.length && (
                <div style={s.attachments}>
                  {item.attachments.map(file => (
                    <a key={file.id} href={file.url} target="_blank" rel="noreferrer" style={s.attachmentLink}>
                      <span style={s.attachmentIcon}>{fileIcon(file)}</span>
                      <span style={s.attachmentName}>{file.original_name}</span>
                      <span style={s.attachmentSize}>{fileSize(file.file_size)}</span>
                    </a>
                  ))}
                </div>
              )}
              <div style={s.cardFoot}>
                <span>{item.creator_name ? `${item.creator_name} · ` : ''}{String(item.updated_at || item.created_at || '').slice(0, 16)}</span>
                <div style={s.actions}>
                  {(active === 'phrase' || active === 'template') && !!item.content && <button style={s.copyBtn} onClick={() => copyText(item)}>复制文字</button>}
                  {canManage && <button style={s.editBtn} onClick={() => openEdit(item)}>编辑</button>}
                  {canManage && <button style={s.deleteBtn} onClick={() => remove(item)}>删除</button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: '#f5f7fa', paddingBottom: '24px' },
  header: { background: 'linear-gradient(135deg,#173f73,#2d7dd2)', color: '#fff', padding: '22px 18px' },
  headerTitle: { fontSize: '24px', fontWeight: 800 },
  headerSub: { marginTop: '5px', fontSize: '13px', opacity: 0.85 },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', padding: '14px' },
  sectionBtn: { minHeight: '74px', border: '1px solid #e6eaf0', borderRadius: '14px', background: '#fff', color: '#333', fontSize: '14px', fontWeight: 700, padding: '10px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px', boxShadow: '0 2px 8px rgba(0,0,0,.05)' },
  sectionBtnActive: { color: '#1a4a8a', border: '2px solid #2d7dd2', background: '#eef6ff' },
  sectionIcon: { fontSize: '24px' },
  toolbar: { margin: '0 14px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' },
  title: { fontSize: '18px', fontWeight: 800, color: '#222' },
  addBtn: { border: 'none', borderRadius: '18px', background: '#1a5da8', color: '#fff', padding: '8px 14px', fontWeight: 700, flexShrink: 0 },
  formCard: { margin: '0 14px 12px', background: '#fff', borderRadius: '14px', padding: '14px', boxShadow: '0 3px 12px rgba(0,0,0,.08)' },
  formTitle: { fontWeight: 800, marginBottom: '10px' },
  input: { width: '100%', boxSizing: 'border-box', border: '1px solid #d9dde5', borderRadius: '9px', padding: '11px', fontSize: '15px', marginBottom: '10px' },
  textarea: { width: '100%', boxSizing: 'border-box', border: '1px solid #d9dde5', borderRadius: '9px', padding: '11px', fontSize: '15px', resize: 'vertical' },
  uploadArea: { marginTop: '10px', padding: '12px', background: '#f7fbff', border: '1px dashed #8bb9e8', borderRadius: '10px' },
  fileBtn: { border: 'none', background: '#e6f4ff', color: '#1668b2', borderRadius: '8px', padding: '9px 12px', fontWeight: 700 },
  fileHint: { color: '#888', fontSize: '12px', marginTop: '7px' },
  selectedList: { marginTop: '8px', color: '#555', fontSize: '12px', lineHeight: 1.7, wordBreak: 'break-all' },
  existingFile: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' },
  removeFileBtn: { border: 'none', background: '#fff1f0', color: '#cf1322', borderRadius: '6px', padding: '3px 7px', flexShrink: 0 },
  formActions: { display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' },
  cancelBtn: { border: '1px solid #ccc', background: '#fff', borderRadius: '8px', padding: '8px 18px' },
  saveBtn: { border: 'none', background: '#1a5da8', color: '#fff', borderRadius: '8px', padding: '8px 20px' },
  list: { padding: '0 14px' },
  card: { background: '#fff', borderRadius: '14px', padding: '15px', marginBottom: '12px', boxShadow: '0 2px 10px rgba(0,0,0,.06)' },
  cardTitle: { fontSize: '17px', fontWeight: 800, color: '#222', marginBottom: '9px' },
  cardContent: { fontSize: '14px', color: '#444', lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  attachments: { display: 'grid', gap: '7px', marginTop: '11px' },
  attachmentLink: { display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 10px', borderRadius: '9px', background: '#f3f8fe', color: '#175d9c', textDecoration: 'none', minWidth: 0 },
  attachmentIcon: { fontSize: '20px', flexShrink: 0 },
  attachmentName: { flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  attachmentSize: { color: '#888', fontSize: '11px', flexShrink: 0 },
  cardFoot: { marginTop: '13px', paddingTop: '10px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', color: '#999', fontSize: '11px' },
  actions: { display: 'flex', gap: '6px', flexShrink: 0 },
  copyBtn: { border: 'none', borderRadius: '7px', background: '#e6f4ff', color: '#1677ff', padding: '5px 9px' },
  editBtn: { border: 'none', borderRadius: '7px', background: '#fff7e6', color: '#d46b08', padding: '5px 9px' },
  deleteBtn: { border: 'none', borderRadius: '7px', background: '#fff1f0', color: '#cf1322', padding: '5px 9px' },
  empty: { textAlign: 'center', color: '#aaa', padding: '45px 10px' }
};
